import { useCallback, useRef } from 'react';

import pluralize from 'pluralize';

import { CAST_AI } from '@cast/constants';
import { useDestructiveDialog } from '@cast/design-system';

import { useRemoveNotFoundClustersMutation } from './useRemoveNotFoundClustersMutation';

type OpenRemoveNotFoundClustersDialogArgs = {
  clusterIds: string[];
  onSuccess?: () => void;
};

export const useRemoveNotFoundClustersDialog = () => {
  const onSuccessRef = useRef<(() => void) | undefined>(undefined);
  const { mutate: removeClusters } = useRemoveNotFoundClustersMutation(() => {
    onSuccessRef.current?.();
  });
  const { openDialog, ...rest } = useDestructiveDialog();

  const openRemoveNotFoundClustersDialog = useCallback(
    ({ clusterIds, onSuccess }: OpenRemoveNotFoundClustersDialogArgs) => {
      onSuccessRef.current = onSuccess;

      const clustersPluralized = pluralize('cluster', clusterIds.length);

      openDialog({
        testId: 'remove-not-found-clusters-dialog',
        title: `Remove selected ${clustersPluralized} from ${CAST_AI}?`,
        message:
          'The clusters will stay operational, but will be removed from the console.',
        confirmButtonText: 'Remove',
        cancelButtonText: 'Cancel',
        onConfirm: () => removeClusters(clusterIds),
        slotProps: {
          cancelButton: { testId: 'cancel-remove-not-found-clusters-button' },
          confirmButton: { testId: 'confirm-remove-not-found-clusters-button' },
        },
      });
    },
    [openDialog, removeClusters]
  );

  return { openDialog: openRemoveNotFoundClustersDialog, ...rest };
};
